import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import WishlistButton from './WishlistButton';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const MyWishlistList = () => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  const token = localStorage.getItem('token');
  const isAuthenticated = !!(user || token);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    }
  }, [isAuthenticated, filter]);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`/api/wishlist/products${params}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        console.log('📦 Wishlist data:', data);
        setWishlist(data || []);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProductImage = (item) => {
    if (item.productId?.images && item.productId.images.length > 0) {
      return `/uploads/${item.productId.images[0]}`;
    }
    return null;
  };

  const getProductName = (item) => {
    return item.productId?.name || 'Unknown Product';
  };

  const getProductPrice = (item) => {
    return item.productId?.price || 0;
  };

  const getProductUnit = (item) => {
    return item.productId?.unit || 'each';
  };

  // Get vendor name - check both producer and hub fields
  const getVendorName = (item) => {
    const product = item.productId;
    if (!product) return 'Vendor';
    
    // Check if it's a hub product (has hubId)
    if (product.hubId) {
      if (typeof product.hubId === 'object' && product.hubId !== null) {
        return product.hubId.hubName || product.hubId.name || 'Hub';
      }
      return 'Hub';
    }
    
    // Check if it's a producer product
    if (product.producer) {
      if (typeof product.producer === 'object' && product.producer !== null) {
        return product.producer.farmName || product.producer.name || 'Producer';
      }
      return 'Producer';
    }
    
    return 'Vendor';
  };

  // Get vendor link
  const getVendorLink = (item) => {
    const product = item.productId;
    if (!product) return '#';
    
    // Hub product
    if (product.hubId) {
      if (typeof product.hubId === 'object' && product.hubId !== null) {
        const slug = product.hubId.slug || product.hubId._id;
        return `/hub/${slug}`;
      }
      return `/hub/${product.hubId}`;
    }
    
    // Producer product
    if (product.producer) {
      if (typeof product.producer === 'object' && product.producer !== null) {
        const slug = product.producer.slug || product.producer._id;
        return `/producer/${slug}`;
      }
      return `/producer/${product.producer}`;
    }
    
    return '#';
  };

  // Get vendor role for link styling
  const getVendorRole = (item) => {
    const product = item.productId;
    if (!product) return 'producer';
    if (product.hubId) return 'hub';
    return 'producer';
  };

  const getProductSlug = (item) => {
    return item.productId?.slug || item.productId?._id;
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-100 text-red-700',
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-yellow-100 text-yellow-700',
      'bg-purple-100 text-purple-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-teal-100 text-teal-700'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please login to see your wishlist</p>
        <Link to="/login" className="text-green-600 hover:underline">Login</Link>
      </div>
    );
  }

  return (
    <div className="my-wishlist-list">
      <div className="flex justify-end mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 animate-pulse bg-gray-50 h-32" />
          ))}
        </div>
      ) : wishlist.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-4xl mb-2">☆</p>
          <p className="text-gray-600">Your wishlist is empty</p>
          <p className="text-sm text-gray-400 mt-1">
            Browse products and click the ⭐ button to save them for later
          </p>
          <Link to="/products" className="inline-block mt-4 text-green-600 hover:underline">
            Browse Products →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wishlist.map((item) => {
            const productName = getProductName(item);
            const vendorName = getVendorName(item);
            const vendorLink = getVendorLink(item);
            const vendorRole = getVendorRole(item);
            const productImage = getProductImage(item);
            const avatarColor = getAvatarColor(productName);
            const initials = getInitials(productName);
            const productSlug = getProductSlug(item);
            const isHub = vendorRole === 'hub';

            return (
              <div key={item._id} className="border rounded-lg p-4 hover:shadow-md transition">
                <div className="grid grid-cols-[auto,1fr,auto] gap-3 items-start">
                  {/* Column 1: Product Image or placeholder */}
                  <div className="flex-shrink-0">
                    {productImage ? (
                      <Link to={`/product/${productSlug}`} title="View details of this product">
                        <img
                          src={productImage}
                          alt={productName}
                          className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                        />
                      </Link>
                    ) : (
                      <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-xl font-bold ${avatarColor}`}>
                        {initials}
                      </div>
                    )}
                  </div>
                  
                  {/* Column 2: Product details */}
                  <div className="min-w-0">
                    <Link 
                      to={`/product/${productSlug}`}
                      className="font-medium hover:text-green-600 hover:underline truncate block"
                      title="View details of this product"
                    >
                      {productName}
                    </Link>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-sm font-semibold text-green-600">
                        ${getProductPrice(item).toFixed(2)}/{getProductUnit(item)}
                      </span>
                    </div>
                    <Link 
                      to={vendorLink}
                      className={`text-xs ${isHub ? 'text-blue-600' : 'text-green-600'} hover:underline mt-1 block truncate`}
                      title={`View ${vendorName}'s storefront`}
                    >
                      {isHub ? '🏪' : '🌱'} by {vendorName}
                    </Link>
                    {item.notes && (
                      <p className="text-xs text-gray-400 mt-1 italic truncate" title={item.notes}>
                        📝 {item.notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Added {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {/* Column 3: Wishlist button */}
                  <div className="flex-shrink-0">
                    <WishlistButton productId={item.productId?._id} size="sm" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyWishlistList;
